import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircleQuestion } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { scrollToSection } from "@/lib/content";
import { RichText } from "@/components/RichText";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Faq() {
  const { t, lang } = useT();
  const [faq, setFaq] = useState(null);

  useEffect(() => {
    api.get("/faq").then((r) => setFaq(r.data)).catch(() => setFaq(null));
  }, []);

  const pl = lang === "pl";
  const items = (faq?.items || []).map((it) => ({ q: pl ? it.q : it.q_en || it.q, a: pl ? it.a : it.a_en || it.a }));
  useEffect(() => {
    if (!items.length) return undefined;
    let el = document.getElementById("ld-faq");
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "ld-faq"; document.head.appendChild(el); }
    el.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: items.map((it) => ({ "@type": "Question", name: it.q, acceptedAnswer: { "@type": "Answer", text: String(it.a).replace(/<[^>]+>/g, "") } })) });
    return () => el?.remove();
  }, [items.length, pl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!items.length) return null;
  const title = pl ? faq.title : faq.title_en || faq.title;

  return (
    <section id="biuro-wirtualne-faq" data-testid="faq-section" className="py-16 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-4"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400 font-semibold">
            <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-red mr-3 align-middle" />{t.nav.faq}
          </p>
          <h2 data-testid="faq-title" className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-4">{title}</h2>
          <button
            type="button"
            data-testid="faq-contact-button"
            onClick={() => scrollToSection("#biuro-wirtualne-kontakt")}
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-tealDark transition-colors"
          >
            <MessageCircleQuestion size={16} /> {t.nav.contact}
          </button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-6 sm:px-8"
        >
          <Accordion type="single" collapsible>
            {items.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold py-5 hover:text-brand-teal hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap"><RichText text={item.a} /></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
